import { FC, useMemo, useState } from 'react'
import { useQuery } from 'react-query'
import { ButtonType, showError } from '../../../../../../../_metronic/helpers'
import { Button, CustomSelect, IOption, Label, useToast } from '../../../../../../components'
import { useAuth } from '../../../../../auth'
import { AtendimentoView } from '../../../../atendimento/core/_models'
import { LotacaoView } from '../../../../funcionarios/funcionarios-list/core/_lotacaoModels'
import { usePermissaoLotacaoLeitura } from '../../../../funcionarios/funcionarios-list/core/_lotacaoPermissao'
import {
  getFuncionarioPessoaEmpresa,
  getLotacoesByFuncionario,
} from '../../../../funcionarios/funcionarios-list/core/_requests'
import { usePermissaoCorrigirLotacao } from '../../../../esus/envios/core/_esusEnvioPermissao'
import {
  corrigirLotacaoSolicitante,
  getLotacoesSolicitante,
} from '../../../../pareceres/pareceres-list/core/_requests'

type Props = {
  atendimento: AtendimentoView
  /** Recarrega o atendimento depois da correção, para o bloco dar lugar à lotação gravada. */
  onCorrigida: () => void
}

/** Mesma regra de `Lotacao.isVigente(referencia)` no backend. */
const vigenteEm = (lotacao: LotacaoView, referencia: Date) =>
  lotacao.ativo &&
  (!lotacao.dataInicio || new Date(lotacao.dataInicio) <= referencia) &&
  (!lotacao.dataFim || new Date(lotacao.dataFim) >= referencia)

const rotuloLotacao = (lotacao: LotacaoView) =>
  [
    lotacao.unidade?.nome,
    lotacao.cbo && `CBO ${lotacao.cbo.codigo}${lotacao.cbo.nome ? ` - ${lotacao.cbo.nome}` : ''}`,
    lotacao.ine && `INE ${lotacao.ine}`,
  ]
    .filter(Boolean)
    .join(' · ')

/**
 * Correção da lotação do solicitante em interconsulta antiga, aberta sem lotação (RATC). Mesma
 * permissão da correção da lotação do responsável (`Atendimento:corrigirLotacao`).
 *
 * De onde vêm as lotações: o próprio solicitante usa a rota dele (só as suas, sem
 * `Funcionario:read`); os demais precisam ler funcionários — pelo vínculo gravado ou, no legado sem
 * ele, pelos vínculos da pessoa na instituição. O backend confere vigência na data da solicitação,
 * unidade e profissional; o filtro daqui só evita oferecer o que ele recusaria.
 */
export const CorrigirLotacaoSolicitante: FC<Props> = ({ atendimento, onCorrigida }) => {
  const { currentUser } = useAuth()
  const { showToast } = useToast()
  const podeCorrigir = usePermissaoCorrigirLotacao()
  const podeLerFuncionarios = usePermissaoLotacaoLeitura()
  const [escolhida, setEscolhida] = useState<IOption<number> | null>(null)
  const [salvando, setSalvando] = useState(false)

  const idEmpresa = atendimento.empresa?.id
  const idSolicitante = atendimento.solicitante?.id
  const idFuncionario = atendimento.idFuncionarioSolicitante
  const proprio = !!idSolicitante && idSolicitante === currentUser?.pessoa?.id
  const habilitado = podeCorrigir && !!idEmpresa && (proprio || podeLerFuncionarios)

  const { data: lotacoes = [], isLoading } = useQuery(
    ['lotacoes-correcao-solicitante', atendimento.id],
    async (): Promise<LotacaoView[]> => {
      if (proprio) return getLotacoesSolicitante(Number(idEmpresa))
      const funcionarios = idFuncionario
        ? [idFuncionario]
        : (await getFuncionarioPessoaEmpresa(idEmpresa, idSolicitante)).map((f) => Number(f.id))
      return (await Promise.all(funcionarios.map(getLotacoesByFuncionario))).flat()
    },
    { enabled: habilitado, retry: false, onError: showError }
  )

  const opcoes = useMemo<IOption<number>[]>(() => {
    const solicitacao = atendimento.solicitacao ? new Date(atendimento.solicitacao) : new Date()
    return lotacoes
      .filter(
        (l) =>
          vigenteEm(l, solicitacao) &&
          (!atendimento.unidade?.id || l.unidade?.id === atendimento.unidade.id)
      )
      .map((l) => ({ value: l.id, label: rotuloLotacao(l) }))
  }, [lotacoes, atendimento.solicitacao, atendimento.unidade?.id])

  if (!habilitado) return null

  const salvar = async () => {
    if (!escolhida) return
    setSalvando(true)
    try {
      await corrigirLotacaoSolicitante(atendimento.id, escolhida.value)
      showToast({ variant: 'success', title: 'Lotação do solicitante corrigida.' })
      onCorrigida()
    } catch (error) {
      showError(error)
    } finally {
      setSalvando(false)
    }
  }

  const inputId = `lotacao-solicitante-${atendimento.id}`

  return (
    <div className='mt-4'>
      <Label htmlFor={inputId} className='pb-2'>
        Lotação do solicitante (não informada na abertura)
      </Label>
      {isLoading ? (
        <p className='text-muted fs-7 mb-0'>Carregando lotações...</p>
      ) : opcoes.length === 0 ? (
        <p className='text-muted fs-7 mb-0'>
          Nenhuma lotação do solicitante estava vigente na data da solicitação
          {atendimento.unidade ? ' nesta unidade' : ''}.
        </p>
      ) : (
        <div className='d-flex flex-wrap gap-3 align-items-center'>
          <div className='flex-grow-1'>
            <CustomSelect
              inputId={inputId}
              options={opcoes}
              value={escolhida ?? undefined}
              placeholder='Selecione a lotação'
              isClearable={false}
              onChange={(opcao: IOption<number> | null) => setEscolhida(opcao)}
            />
          </div>
          <Button
            type={ButtonType.Button}
            className='btn-primary'
            disabled={!escolhida || salvando}
            onClick={salvar}
          >
            Salvar lotação
          </Button>
        </div>
      )}
    </div>
  )
}
